import { getDecayCalibrationTypeName } from '@components/export/spread/decay/decay-change-logs';
import { styleRangeBasedOnCalibrationStatus } from '@components/export/spread/helpers';
import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { IBetaCalibration } from '@models/beta/beta-estimate';
import { IMarketRate } from '@models/beta/marketrate';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IDecayEstimate } from '@models/decay/decay-estimate';
import { functionalFormsDecay } from '@models/functional-form';
import { IProduct } from '@models/product';
import { CalibrationFilter } from '@models/record-status';
import { ForecastMethods } from '@models/stable-balance/forecast-settings';
import { PolynomialForms } from '@models/stable-balance/polynomial-forms';
import { IStableBalanceCalibration } from '@models/stable-balance/stable-balance-calibration';
import { getDateString, getMonthYear } from '@shared/dates';
import { getForecastMethodName } from '@shared/get-forecast-method-name';
import { autofitColumns, autofitRows } from '@shared/spread/sheet-functions';
import { mapActionKeys, setGreenArea } from '@shared/spread/spread-action-keys';
import { SpreadContextMenu } from '@shared/spread/spread-context-menu';
import { borders, colours, DASH, defaultBookOptions, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';

const paddingRows = 0;
const colWidth = 20;
const maxProductsWidth = 300;
const maxColWidth = 130;
const rowHeight = 20;
const maxRowHeight = 500;
const page = 'calibration-assumptions';
export const START_BETA_COL = 1;
export const START_DECAY_COL = 4;
export const START_SBAL_COL = 12;
export const START_HISTORICAL_SETTINGS = 16;
export const START_FORECAST_SETTINGS = 22;
export const BETA_BLOCK_SIZE = 3;
export const DECAY_BLOCK_SIZE = 8;
export const SBAL_BLOCK_SIZE = 14;

export const CALIBRATION_ASSUMPTIONS_HEADERS = [
  'Product',
  // Beta
  'Coeff.',
  'Market Rate',
  'Lag',
  // Decay
  'Calibration\nType',
  'Excluded\nAcquisitions',
  'Vintage\nStart',
  'Last\nObservation',
  'Model',
  'Start\nObs.',
  'End\nObs.',
  'Curtailment',
  // Stable Balance,
  'Calibration\nStart',
  'Calibration\nEnd',
  'Polynomial\nForm',
  'Selected\nAcquisitions',
  // Historical Settings,
  'Std. Dev.',
  'Std. Dev. Mul.',
  'Damping',
  'Haircut',
  'Damp ▲',
  'Damp ▼',
  // Forecast Settings,
  'Method',
  'Growth',
  'Damp ▲',
  'Damp ▼'
];

const getFont = () => `${parseInt(FONT_SIZE, 10) - 1}pt ${FONT_NAME}`;

// ==================================================================================================================================
//
// ==================================================================================================================================

export const workbookInit = args => {
  const spread: GC.Spread.Sheets.Workbook = args.spread;
  return spread;
};

export const setUpWorkbook = (spread: GC.Spread.Sheets.Workbook, sheet: GC.Spread.Sheets.Worksheet) => {
  sheet.suspendPaint();
  sheet.suspendEvent();

  spread.options = { ...spread.options, ...defaultBookOptions };
  spread.options.allowUserDragFill = false;

  SpreadContextMenu.setCopyCutPaste(spread);
  GC.Spread.Sheets.Commands.autoFitColumn.canUndo = false;
  GC.Spread.Sheets.Commands.autoFitRow.canUndo = false;

  // Sheet Options
  sheet.name(page);
  sheet.defaults.colWidth = colWidth;
  sheet.defaults.rowHeight = maxRowHeight;
  sheet.options.allowCellOverflow = true;
  sheet.options.clipBoardOptions = GC.Spread.Sheets.ClipboardPasteOptions.values;
  sheet.options.colHeaderVisible = true;
  sheet.options.frozenlineColor = 'transparent';

  // Sheet Protection Options
  sheet.options.isProtected = true;
  sheet.options.protectionOptions = {
    ...sheet.options.protectionOptions,
    allowDeleteColumns: false,
    allowDeleteRows: false,
    allowDragInsertColumns: false,
    allowDragInsertRows: false,
    allowEditObjects: false,
    allowFilter: false,
    allowInsertColumns: false,
    allowInsertRows: false,
    allowResizeColumns: true,
    allowResizeRows: true,
    allowSelectLockedCells: true,
    allowSelectUnlockedCells: true,
    allowSort: false
  };

  // Initial Styling before populating data
  populateHeaders(sheet, GC.Spread.Sheets.SheetArea.colHeader);
  setSheetStyling(sheet, []);

  setGreenArea({ page, startRow: 0, startCol: 0, paddingRows, paddingCols: 0 });
  mapActionKeys(spread, sheet, page);

  sheet.resumePaint();
  sheet.resumeEvent();
};

const setSheetStyling = (sheet: GC.Spread.Sheets.Worksheet, products: IProduct[] = []): void => {
  if (sheet) {
    sheet.defaults.colWidth = colWidth;
    const header = GC.Spread.Sheets.SheetArea.colHeader;

    // Clear previous data and styling
    sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount()).clear(GC.Spread.Sheets.StorageType.style);
    sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount()).clear(GC.Spread.Sheets.StorageType.data);

    const numOfProducts = products.length || 30;
    sheet.setRowCount(numOfProducts + paddingRows);
    sheet.setColumnCount(26);

    sheet.frozenColumnCount(1);

    const headerRange = sheet.getRange(0, 0, 3, sheet.getColumnCount(), header);
    const betaHeaderRange = sheet.getRange(0, START_BETA_COL, 3, BETA_BLOCK_SIZE, header);
    const stableBalanceHeaderRange = sheet.getRange(0, START_SBAL_COL, 3, SBAL_BLOCK_SIZE, header);

    const fullRange = sheet.getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount());
    const calibrationsRange = sheet.getRange(0, 1, numOfProducts, sheet.getColumnCount() - 1);
    const productColumn = sheet.getRange(0, 0, numOfProducts, 1);

    const smallTextColumns = [];
    smallTextColumns.forEach(col => {
      sheet.getRange(0, col, numOfProducts, 1).font(getFont());
    });

    fullRange.locked(true);
    fullRange.cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`).setBorder(borders.SUBTLE, { all: true });
    fullRange.vAlign(GC.Spread.Sheets.VerticalAlign.center);
    fullRange.wordWrap(true);
    calibrationsRange.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

    productColumn.backColor(colours.LIGHT_BLUE).cellPadding(`0 ${FONT_SIZE}`);
    calibrationsRange.backColor(colours.WHITE).cellPadding(`0 ${FONT_SIZE}`);

    // HEADER STYLING
    const headerRow1st = sheet.getRange(0, 0, 1, sheet.getColumnCount(), header);
    const headerRowLast = sheet.getRange(3, 0, 1, sheet.getColumnCount(), header);
    headerRange
      .foreColor(colours.MAIN)
      .backColor(colours.MENU_BG)
      .height(35);
    headerRange.wordWrap(true);
    headerRange.cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`);

    betaHeaderRange.backColor(colours.MENU_BG2);
    stableBalanceHeaderRange.backColor(colours.MENU_BG2);

    headerRange.font(getFont());
    headerRowLast.borderBottom(borders.BLACK_MEDIUM);
    headerRow1st.font(`bold ${parseInt(FONT_SIZE, 10) + 1}pt ${FONT_NAME}`);

    setLeftBorders(sheet, [1, START_BETA_COL, START_DECAY_COL, START_SBAL_COL, START_FORECAST_SETTINGS, START_HISTORICAL_SETTINGS]);
    setBordersBetweenProductTabs(sheet, products);
  }
};

const setLeftBorders = (sheet: GC.Spread.Sheets.Worksheet, cols: number[]) => {
  cols.forEach((col, i) => {
    sheet.getRange(0, col, 3, 1, GC.Spread.Sheets.SheetArea.colHeader).borderLeft(borders.SUBTLE_DARK);
    sheet.getRange(0, col, sheet.getRowCount() - paddingRows, 1).borderLeft(borders.SUBTLE_DARK);

    // Setting WHITE2 background to odd column ranges
    if (i > 1 && i % 2 !== 0) {
      const ncols = cols[i] - cols[i - 1];
      sheet.getRange(0, col - ncols, sheet.getRowCount() - paddingRows, ncols).backColor(colours.WHITE2);
    }
  });
};

const setBordersBetweenProductTabs = (sheet: GC.Spread.Sheets.Worksheet, products: IProduct[] = []) => {
  for (let i = 1; i < products.length; i++) {
    const product = products[i];
    const productPrev = products[i - 1];
    if (product.TabId !== productPrev.TabId) {
      sheet.getRange(i, 0, 1, sheet.getColumnCount()).borderTop(borders.SUBTLE_DARK);
    }
  }
};

export const populateSpreadsheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  products: IProduct[] = [],
  acquisitions: IAcquisition[],
  getAcquisitionNames: (acquisitions: any, acquisitionIds: number[] | string) => string,
  marketRates: IMarketRate[],
  calibrationFilter: CalibrationFilter,
  betaCalibrations: IBetaCalibration[],
  decayCalibrations: IDecayCalibration[],
  stableBalanceCalibrations: IStableBalanceCalibration[],
  lastObservationDate: Date,
  decayCalibrationTypes: IDecayCalibrationType[]
) => {
  try {
    sheet.suspendPaint();

    populateHeaders(sheet, GC.Spread.Sheets.SheetArea.colHeader);
    setSheetStyling(sheet, products);

    populateProductNames(sheet, products);
    populateBeta(sheet, products, calibrationFilter, betaCalibrations, marketRates);
    populateDecay(
      sheet,
      products,
      calibrationFilter,
      decayCalibrations,
      lastObservationDate,
      getAcquisitionNames,
      acquisitions,
      decayCalibrationTypes
    );
    populateStableBalance(sheet, products, calibrationFilter, stableBalanceCalibrations, getAcquisitionNames, acquisitions);

    // Autofit all columns
    autofitColumns(sheet, 0, 0, colWidth, maxProductsWidth); // Products
    autofitColumns(sheet, 1, null, colWidth, maxColWidth, [2, 6]);

    // Autofit all rows
    autofitRows(sheet, 0, null, rowHeight);

    setGreenArea({ page });

    sheet.resumePaint();
  } catch (error) {
    console.warn(error);
  }
};

export const populateHeaders = (sheet: GC.Spread.Sheets.Worksheet, sheetArea?: GC.Spread.Sheets.SheetArea) => {
  // Set the row count of column header to 3.
  if (sheetArea === GC.Spread.Sheets.SheetArea.colHeader) {
    sheet.setRowCount(3, sheetArea);
  }

  // Level 1 headers
  sheet.addSpan(0, 0, 3, 1, sheetArea);
  sheet.setValue(0, 0, CALIBRATION_ASSUMPTIONS_HEADERS[0], sheetArea);

  sheet.addSpan(0, START_BETA_COL, 1, BETA_BLOCK_SIZE, sheetArea);
  sheet.setValue(0, START_BETA_COL, 'Beta', sheetArea);

  sheet.addSpan(0, START_DECAY_COL, 1, DECAY_BLOCK_SIZE, sheetArea);
  sheet.setValue(0, START_DECAY_COL, 'Decay', sheetArea);

  sheet.addSpan(0, START_SBAL_COL, 1, SBAL_BLOCK_SIZE, sheetArea);
  sheet.setValue(0, START_SBAL_COL, 'Stable Balance', sheetArea);

  // Level 2 headers: Beta, Decay, Stable Balance Calibration and Stable Balance Setting headers
  for (let i = START_BETA_COL; i < START_HISTORICAL_SETTINGS; i++) {
    sheet.addSpan(1, i, 2, 1, sheetArea);
    sheet.setValue(1, i, CALIBRATION_ASSUMPTIONS_HEADERS[i], sheetArea);
  }

  sheet.addSpan(1, START_HISTORICAL_SETTINGS, 1, 6, sheetArea);
  sheet.setValue(1, START_HISTORICAL_SETTINGS, 'Historical Settings', sheetArea);

  sheet.addSpan(1, START_FORECAST_SETTINGS, 1, 4, sheetArea);
  sheet.setValue(1, START_FORECAST_SETTINGS, 'Forecast Settings', sheetArea);

  // Level 3 headers: Historican and Forecast Settings
  for (let i = START_HISTORICAL_SETTINGS; i < CALIBRATION_ASSUMPTIONS_HEADERS.length; i++) {
    sheet.setValue(2, i, CALIBRATION_ASSUMPTIONS_HEADERS[i], sheetArea);
  }
};

const populateProductNames = (sheet: GC.Spread.Sheets.Worksheet, products: IProduct[]): void => {
  if (sheet && products && products.length > 0) {
    const productNames = products.map(product => `${product.ProductName}`);
    sheet.setArray(0, 0, productNames);
  }
};

const populateBeta = (
  sheet: GC.Spread.Sheets.Worksheet,
  products: IProduct[],
  calibrationFilter: CalibrationFilter,
  betaCalibrations: IBetaCalibration[],
  marketRates: IMarketRate[]
) => {
  if (sheet && betaCalibrations && betaCalibrations.length > 0) {
    const getMarketRateName = (marketRateId: number): string => {
      if (marketRates && marketRates.length > 0 && marketRateId) {
        const marketRate = marketRates.find(({ MarketRateId }) => MarketRateId === marketRateId);
        return marketRate ? marketRate.MarketRateName : marketRateId.toString();
      } else {
        return marketRateId.toString();
      }
    };

    products.forEach((product, row) => {
      const calibration: IBetaCalibration = betaCalibrations.find(c => c.ProductId === product.ProductId && calibrationFilter(c));
      if (calibration) {
        let currentColumn = START_BETA_COL;
        sheet.setValue(row, currentColumn, Number(calibration.BetaCoefficient).toFixed(2));
        sheet.setValue(row, ++currentColumn, getMarketRateName(calibration.MarketRateId));
        sheet.setValue(row, ++currentColumn, calibration.Lag);

        // Styling based on Calibration Status
        const rowRange = sheet.getRange(row, START_BETA_COL, 1, BETA_BLOCK_SIZE);
        styleRangeBasedOnCalibrationStatus(rowRange, calibration.RecordStatus);
        rowRange.font(getFont());
      }
    });
  }
};

const populateDecay = (
  sheet: GC.Spread.Sheets.Worksheet,
  products: IProduct[],
  calibrationFilter: CalibrationFilter,
  decayCalibrations: IDecayCalibration[],
  lastObservationDate: Date,
  getAcquisitionNames: (acquisitions: any, acquisitionIds: number[] | string) => string,
  acquisitions: IAcquisition[],
  decayCalibrationTypes: IDecayCalibrationType[]
) => {
  if (sheet && decayCalibrations && decayCalibrations.length > 0) {
    products.forEach((product, row) => {
      const calibration: IDecayCalibration = decayCalibrations.find(c => c.ProductId === product.ProductId && calibrationFilter(c));
      if (calibration) {
        let estimateModel = null;
        const estimate: IDecayEstimate = calibration.DecayEstimateSetting ? calibration.DecayEstimateSetting.find(e => e.IsChosen) : null;

        if (estimate) {
          const functionalForm = functionalFormsDecay.find(ff => ff.Index === estimate.FunctionalForm);
          estimateModel = functionalForm ? functionalForm.Name : estimate.FunctionalForm;
        }

        let currentColumn = START_DECAY_COL;
        sheet.setValue(row, currentColumn, getDecayCalibrationTypeName(calibration.DecayCalibrationType, decayCalibrationTypes));
        sheet.setValue(row, ++currentColumn, getAcquisitionNames(acquisitions, calibration.ExcludeAcquisitionIds) || DASH);
        sheet.setValue(row, ++currentColumn, getMonthYear(calibration.StartDate) || DASH);
        sheet.setValue(row, ++currentColumn, getMonthYear(lastObservationDate) || DASH);

        sheet.setValue(row, ++currentColumn, estimateModel || DASH);
        sheet.setValue(row, ++currentColumn, estimate ? estimate.StartObservation || DASH : DASH);
        sheet.setValue(row, ++currentColumn, estimate ? estimate.EndObservation || DASH : DASH);
        sheet.setValue(row, ++currentColumn, estimate ? estimate.Curtailment || DASH : calibration.ImpliedCurtailment || DASH);

        // Styling based on Calibration Status
        const rowRange = sheet.getRange(row, START_DECAY_COL, 1, DECAY_BLOCK_SIZE);
        styleRangeBasedOnCalibrationStatus(rowRange, calibration.RecordStatus);
        rowRange.font(getFont());
      }
    });
  }
};

const populateStableBalance = (
  sheet: GC.Spread.Sheets.Worksheet,
  products: IProduct[],
  calibrationFilter: CalibrationFilter,
  stableBalanceCalibrations: IStableBalanceCalibration[],
  getAcquisitionNames: (acquisitions: any, acquisitionIds: number[] | string) => string,
  acquisitions: IAcquisition[]
) => {
  if (sheet && stableBalanceCalibrations && stableBalanceCalibrations.length > 0) {
    products.forEach((product, row) => {
      const calibration: IStableBalanceCalibration = stableBalanceCalibrations.find(
        c => c.ProductId === product.ProductId && calibrationFilter(c)
      );
      if (calibration) {
        const haircut = calibration.Settings.Historical.Haircut;
        let currentColumn = START_SBAL_COL;

        sheet.setValue(row, currentColumn, getMonthYear(calibration.CalibrationStartDate));
        sheet.setValue(row, ++currentColumn, getMonthYear(calibration.CalibrationEndDate));
        sheet.setValue(row, ++currentColumn, haircut ? DASH : PolynomialForms[calibration.SelectedPolynomialForm]);
        sheet.setValue(
          row,
          ++currentColumn,
          haircut ? DASH : getAcquisitionNames(acquisitions, calibration.SelectedAcquisitionIds) || DASH
        );

        // Historical Settings
        sheet.setValue(row, ++currentColumn, haircut ? DASH : calibration.Settings.Historical.StdTerm);
        sheet.setValue(row, ++currentColumn, haircut ? DASH : calibration.Settings.Historical.StdMultiplier);
        sheet.setValue(row, ++currentColumn, haircut ? DASH : calibration.Settings.Historical.Damping ? 'True' : 'False');

        if (haircut) {
          sheet.setValue(row, ++currentColumn, `100 %`);
        } else {
          const nonStablePercent = calibration.NonStablePercents.find(
            b => b.IsHistorical && getDateString(b.PeriodEndDate) === getDateString(calibration.CalibrationEndDate)
          );
          const endOfTs = 100 * nonStablePercent.Balance;

          sheet.setValue(row, ++currentColumn, `${0 + Number(endOfTs.toFixed())} %`);
        }

        sheet.setValue(row, ++currentColumn, haircut ? DASH : `${(calibration.Settings.Historical.HistoricalUpDamper * 100).toFixed()} %`);
        sheet.setValue(
          row,
          ++currentColumn,
          haircut ? DASH : `${Math.abs(calibration.Settings.Historical.HistoricalDownDamper * 100).toFixed()} %`
        );

        const method = getForecastMethodName(calibration.Settings.Forecast.ForecastMethod);

        // Forecast Settings
        const growthValue =
          method === getForecastMethodName(ForecastMethods['Haircut %'])
            ? DASH
            : `${(calibration.Settings.Forecast.ForecastGrowth * 100).toFixed()} %`;
        const upDamperValue =
          method === getForecastMethodName(ForecastMethods['Haircut %'])
            ? `${(calibration.Settings.Forecast.ForecastUpDamper * 100).toFixed()} %`
            : DASH;
        const downDamperValue =
          method === getForecastMethodName(ForecastMethods['Haircut %'])
            ? `${Math.abs(calibration.Settings.Forecast.ForecastDownDamper * 100).toFixed()} %`
            : DASH;

        sheet.setValue(row, ++currentColumn, method);
        sheet.setValue(row, ++currentColumn, growthValue);
        sheet.setValue(row, ++currentColumn, upDamperValue);
        sheet.setValue(row, ++currentColumn, downDamperValue);

        // Styling based on Calibration Status
        const rowRange = sheet.getRange(row, START_SBAL_COL, 1, SBAL_BLOCK_SIZE);
        styleRangeBasedOnCalibrationStatus(rowRange, calibration.RecordStatus);
        rowRange.font(getFont());
      }
    });
  }
};
